import type { Maybe } from '@amazon/vinyl-util'
import { sanitizeAllowlistedHtml, type HtmlAllowlist } from '@/util/allowlistHtml'

/** The tags Green Hornet's `RichText` renders (`HTMLTagAllowList`); any other tag goes with its content. */
const RICH_TEXT_TAGS: ReadonlySet<string> = new Set([
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'p',
    'hr',
    'blockquote',
    'ol',
    'ul',
    'li',
    'dl',
    'dt',
    'dd',
    'div',
    'br',
    'a',
    'b',
    'i',
    's',
    'small',
    'sub',
    'sup',
    'em',
    'strong',
    'u',
    'mark',
    'q',
    'cite',
    'code',
    'var',
])

const LINK_SCHEMES: ReadonlySet<string> = new Set(['http', 'https', 'mailto', 'tel'])

// Browsers ignore whitespace and control characters inside a scheme, so `java\tscript:` must not pass.
function withoutIgnoredUrlCharacters(href: string): string {
    return Array.from(href)
        .filter((char) => {
            const code = char.charCodeAt(0)
            return code > 0x20 && (code < 0x7f || code > 0x9f)
        })
        .join('')
}

function isSafeLinkUrl(href: string): boolean {
    const scheme = /^([a-z][a-z0-9+.-]*):/.exec(withoutIgnoredUrlCharacters(href).toLowerCase())
    return !scheme || LINK_SCHEMES.has(scheme[1])
}

// GH's `richTextConfig` visitor: `<p></p>`, `<p> </p>` and `<p><br></p>` leave no blank gap.
function isEmptyParagraph(el: Element, tag: string): boolean {
    if (tag !== 'p') return false
    if (el.childNodes.length === 0) return true
    if (el.childNodes.length !== 1) return false
    const only = el.childNodes[0]
    return only.nodeType === 3
        ? (only.textContent ?? '').trim() === ''
        : only.nodeType === 1 && (only as Element).tagName.toLowerCase() === 'br'
}

function richTextAllowlist(dropEmptyParagraphs: boolean): HtmlAllowlist {
    return {
        tags: RICH_TEXT_TAGS,
        dropDisallowed: true,
        attribute: (tag, name) => tag === 'a' && name === 'href',
        keep: (el, tag) => {
            if (tag !== 'a') return true
            const href = el.getAttribute('href')
            if (href === null) return true
            if (!isSafeLinkUrl(href)) {
                el.removeAttribute('href')
                return true
            }
            el.setAttribute('target', '_blank')
            el.setAttribute('rel', 'noopener noreferrer')
            return true
        },
        ...(dropEmptyParagraphs ? { drop: isEmptyParagraph } : null),
    }
}

const WITH_EMPTY_PARAGRAPHS = richTextAllowlist(false)
const WITHOUT_EMPTY_PARAGRAPHS = richTextAllowlist(true)

export interface RichTextSanitizeOptions {
    /** GH's podcast show and episode headers drop empty paragraphs. */
    readonly dropEmptyParagraphs?: true
}

/**
 * Server rich text (podcast and merch descriptions, legal copy) reduced to what GH renders: its
 * allowlisted tags, with links the only attributes and only web, mail and phone links kept.
 */
export function sanitizeRichText(
    html: Maybe<string>,
    options: RichTextSanitizeOptions = {}
): string {
    return sanitizeAllowlistedHtml(
        html ?? '',
        options.dropEmptyParagraphs ? WITHOUT_EMPTY_PARAGRAPHS : WITH_EMPTY_PARAGRAPHS
    )
}
